<section class="bg-star-gray-600 pt-[40px] pb-[60px] lg:pt-[64px] lg:pb-[80px]">
  <div
    class="mx-auto flex max-w-[1000px] flex-col gap-[40px] px-[20px] lg:flex-row lg:items-center lg:gap-[48px]"
  >
    <div class="flex-1 lg:flex-[1.45]">
      <h1 class="text-[30px] font-medium leading-[1.25] text-white lg:text-[38px] xl:text-[44px]">
        Crypto Wallet Screening for Compliance Teams
      </h1>
      <p class="mt-[20px] text-[16px] leading-[1.8] text-white/80">
        Wallet screening asks one question about a crypto address: what has it been close to?
        AMLOfficer screens an address against four AML data providers at once and returns a single
        risk picture — in the dashboard, in your team's Telegram group, or inside your own systems.
      </p>
      <a
        [href]="createAccountHref()"
        target="_blank"
        class="btn btn-white btn-with-arrow mt-[28px] min-h-[52px] w-full max-w-[300px] rounded-[8px] font-harmony font-semibold leading-[18px]"
      >
        <span class="text-[16px] tracking-[0.3px]">Open Business Account</span>
        <mat-icon
          svgIcon="second-arrow-right"
          class="ml-[13px] mr-[5px] max-h-[14px] max-w-[16px] text-black"
        ></mat-icon>
      </a>
      <p class="mt-[16px] text-[14px] leading-[1.6] text-white/50">
        Screening runs inside the AMLOfficer application, so a business account is the first step.
        What you pay depends on your volumes and is shown in your account after registration.
      </p>
    </div>
    <div class="flex flex-1 justify-center">
      <img
        src="./images/aml-providers.webp"
        alt="Illustration of AML wallet reports opened in a dashboard"
        width="337"
        height="213"
        class="w-full max-w-[337px] object-contain"
      />
    </div>
  </div>
</section>

<section class="bg-star-gray-600 pb-[60px] lg:pb-[80px]">
  <div class="mx-auto max-w-[1000px] px-[20px]">
    <h2 class="text-[24px] font-medium leading-[1.3] text-white lg:text-[28px] xl:text-[32px]">
      What wallet screening is
    </h2>
    <p class="mt-[20px] text-[16px] leading-[1.8] text-white/80">
      Every crypto address carries a public history, and that history is the only thing you can
      examine before you deal with the person behind it. Wallet screening reads that history through
      AML data providers and turns it into something a compliance team can act on: an assessment of
      the risk the address is exposed to, and the flows it took part in.
    </p>
    <p class="mt-[16px] text-[16px] leading-[1.8] text-white/80">
      The result describes an address, not a person. It tells you what the funds have been near and
      how much of that exposure would move onto your side if you accept them. That distinction
      matters in practice, because the moment to weigh an address is while it is still your
      counterparty's problem rather than an entry in your own ledger.
    </p>
  </div>
</section>

<section class="bg-star-gray-600 pb-[60px] lg:pb-[80px]">
  <div class="mx-auto max-w-[1000px] px-[20px]">
    <h2 class="text-[24px] font-medium leading-[1.3] text-white lg:text-[28px] xl:text-[32px]">
      What comes back on a screened address
    </h2>
    <ul class="mt-[24px] flex flex-col gap-[16px]">
      <li class="flex items-start gap-[12px] text-[16px] leading-[1.7] text-white/80">
        <mat-icon
          svgIcon="check-circle-white-marker"
          class="mt-[6px] !h-auto max-h-[20px] !w-full max-w-[20px] shrink-0 text-[#2E9478]"
        ></mat-icon>
        <span
          ><span class="text-white">Risk data from several sources at once</span>, rather than the
          reading of a single vendor you happen to have a contract with.</span
        >
      </li>
      <li class="flex items-start gap-[12px] text-[16px] leading-[1.7] text-white/80">
        <mat-icon
          svgIcon="check-circle-white-marker"
          class="mt-[6px] !h-auto max-h-[20px] !w-full max-w-[20px] shrink-0 text-[#2E9478]"
        ></mat-icon>
        <span
          ><span class="text-white">History at the depth you choose</span> — incoming funds,
          outgoing funds, or the address's full transaction history when a shallow look is not
          enough.</span
        >
      </li>
      <li class="flex items-start gap-[12px] text-[16px] leading-[1.7] text-white/80">
        <mat-icon
          svgIcon="check-circle-white-marker"
          class="mt-[6px] !h-auto max-h-[20px] !w-full max-w-[20px] shrink-0 text-[#2E9478]"
        ></mat-icon>
        <span
          ><span class="text-white">Counterparty exposure</span> — the flows the address has been
          part of, which is what pre-trade and pre-settlement due diligence actually turns on.</span
        >
      </li>
      <li class="flex items-start gap-[12px] text-[16px] leading-[1.7] text-white/80">
        <mat-icon
          svgIcon="check-circle-white-marker"
          class="mt-[6px] !h-auto max-h-[20px] !w-full max-w-[20px] shrink-0 text-[#2E9478]"
        ></mat-icon>
        <span
          ><span class="text-white">A wallet compliance report you can keep</span>, so the reasoning
          behind a decision survives the day it was made and can be produced later.</span
        >
      </li>
      <li class="flex items-start gap-[12px] text-[16px] leading-[1.7] text-white/80">
        <mat-icon
          svgIcon="check-circle-white-marker"
          class="mt-[6px] !h-auto max-h-[20px] !w-full max-w-[20px] shrink-0 text-[#2E9478]"
        ></mat-icon>
        <span
          ><span class="text-white">The option to keep the address</span> under automated watch
          instead of screening it by hand every time it comes back.</span
        >
      </li>
    </ul>
    <p class="mt-[24px] text-[16px] leading-[1.8] text-white/80">
      What screening does not do is name the owner of an address. It works on the blockchain side of
      the picture; establishing who your customer is stays with the identity checks you run
      elsewhere.
    </p>
  </div>
</section>

<section class="bg-star-gray-600 pb-[60px] lg:pb-[80px]">
  <div class="mx-auto max-w-[1000px] px-[20px]">
    <h2 class="text-[24px] font-medium leading-[1.3] text-white lg:text-[28px] xl:text-[32px]">
      Screening an address, checking a transfer, watching a wallet
    </h2>
    <p class="mt-[20px] text-[16px] leading-[1.8] text-white/80">
      These three are often sold as one thing, and treating them as one is how teams end up with
      gaps. They differ in what you are asking about.
    </p>
    <div class="mt-[28px] grid gap-[20px] md:grid-cols-3">
      <div class="rounded-[10px] bg-[#25282D] px-[20px] pb-[24px] pt-[20px]">
        <h3 class="text-[20px] font-bold leading-[1.3] text-white">A one-off check</h3>
        <p class="mt-[10px] text-[15px] leading-[1.7] text-white/70">
          One question about one transfer or one address, answered once. Enough for a counterparty
          you will never see again.
        </p>
      </div>
      <div class="rounded-[10px] bg-[#25282D] px-[20px] pb-[24px] pt-[20px]">
        <h3 class="text-[20px] font-bold leading-[1.3] text-white">Wallet screening</h3>
        <p class="mt-[10px] text-[15px] leading-[1.7] text-white/70">
          The address itself is the subject, examined to the depth the decision deserves. This is
          what you run when the counterparty, not a single payment, is what you are deciding about.
        </p>
      </div>
      <div class="rounded-[10px] bg-[#25282D] px-[20px] pb-[24px] pt-[20px]">
        <h3 class="text-[20px] font-bold leading-[1.3] text-white">Continuous monitoring</h3>
        <p class="mt-[10px] text-[15px] leading-[1.7] text-white/70">
          The address stays under watch after the answer, and the platform comes back to you when
          its activity changes rather than waiting to be asked again.
        </p>
      </div>
    </div>
    <p class="mt-[24px] text-[16px] leading-[1.8] text-white/80">
      In AMLOfficer they are the same workflow at different settings: an address you screened once
      can be handed over to automated real-time monitoring without being re-entered anywhere.
    </p>
  </div>
</section>

<section class="bg-star-gray-600 pb-[60px] lg:pb-[80px]">
  <div class="mx-auto max-w-[1000px] px-[20px]">
    <h2 class="text-[24px] font-medium leading-[1.3] text-white lg:text-[28px] xl:text-[32px]">
      Four AML data providers behind one screening request
    </h2>
    <p class="mt-[20px] text-[16px] leading-[1.8] text-white/80">
      AML providers do not work from identical datasets. An address that looks unremarkable to one
      of them can carry a flag with another, and a screening result built on a single source quietly
      inherits that source's blind spots along with its coverage.
    </p>
    <p class="mt-[16px] text-[16px] leading-[1.8] text-white/80">
      AMLOfficer aggregates four AML data providers, including BitOK, Global Ledger and Beosin, and
      one screening request reaches all of them. Where they differ, your team sees the difference
      and decides what to do with it — which is a better position than never learning that a second
      opinion existed.
    </p>
  </div>
</section>

<section class="bg-star-gray-600 pb-[60px] lg:pb-[80px]">
  <div class="mx-auto max-w-[1000px] px-[20px]">
    <h2 class="text-[24px] font-medium leading-[1.3] text-white lg:text-[28px] xl:text-[32px]">
      Where screening actually happens
    </h2>
    <div class="mt-[28px] grid gap-[20px] md:grid-cols-3">
      <div class="rounded-[10px] bg-[#25282D] px-[20px] pb-[24px] pt-[20px]">
        <h3 class="text-[20px] font-bold leading-[1.3] text-white">In the dashboard</h3>
        <p class="mt-[10px] text-[15px] leading-[1.7] text-white/70">
          Add an address, choose the scope, read the report and keep it. Colleagues you invite work
          on the same wallets under their own roles.
        </p>
      </div>
      <div class="rounded-[10px] bg-[#25282D] px-[20px] pb-[24px] pt-[20px]">
        <h3 class="text-[20px] font-bold leading-[1.3] text-white">In Telegram</h3>
        <p class="mt-[10px] text-[15px] leading-[1.7] text-white/70">
          Generate a wallet compliance report and receive alerts in a few taps, including
          notifications to the group chat your compliance and operations people already share.
        </p>
      </div>
      <div class="rounded-[10px] bg-[#25282D] px-[20px] pb-[24px] pt-[20px]">
        <h3 class="text-[20px] font-bold leading-[1.3] text-white">Through the API</h3>
        <p class="mt-[10px] text-[15px] leading-[1.7] text-white/70">
          Screen an address at the point where your own product makes the decision, and pull AML
          data into your systems in real time for advanced use cases.
        </p>
      </div>
    </div>
  </div>
</section>

<section class="bg-star-gray-600 pb-[60px] lg:pb-[80px]">
  <div class="mx-auto max-w-[1000px] px-[20px]">
    <h2 class="text-[24px] font-medium leading-[1.3] text-white lg:text-[28px] xl:text-[32px]">
      The moments a team screens an address
    </h2>
    <p class="mt-[20px] text-[16px] leading-[1.8] text-white/80">
      Screening earns its place at the points where something is about to become irreversible.
    </p>
    <div class="mt-[28px] grid gap-[20px] md:grid-cols-2">
      <div class="rounded-[10px] bg-[#25282D] px-[20px] pb-[24px] pt-[20px]">
        <h3 class="text-[19px] font-bold leading-[1.3] text-white">Before a deposit is credited</h3>
        <p class="mt-[10px] text-[15px] leading-[1.7] text-white/70">
          The sending address is screened while the funds are still unaccepted, so a risky deposit
          is a question rather than a balance you now have to explain.
        </p>
      </div>
      <div class="rounded-[10px] bg-[#25282D] px-[20px] pb-[24px] pt-[20px]">
        <h3 class="text-[19px] font-bold leading-[1.3] text-white">Before a payout leaves</h3>
        <p class="mt-[10px] text-[15px] leading-[1.7] text-white/70">
          Withdrawal destinations get the same treatment as incoming funds, because where your
          assets go is part of your exposure too.
        </p>
      </div>
      <div class="rounded-[10px] bg-[#25282D] px-[20px] pb-[24px] pt-[20px]">
        <h3 class="text-[19px] font-bold leading-[1.3] text-white">Before an OTC settlement</h3>
        <p class="mt-[10px] text-[15px] leading-[1.7] text-white/70">
          Large trades are the classic case for looking at a counterparty's transaction history in
          full rather than at the single transfer in front of you.
        </p>
      </div>
      <div class="rounded-[10px] bg-[#25282D] px-[20px] pb-[24px] pt-[20px]">
        <h3 class="text-[19px] font-bold leading-[1.3] text-white">
          When onboarding a counterparty
        </h3>
        <p class="mt-[10px] text-[15px] leading-[1.7] text-white/70">
          A merchant, a partner or a client wallet is screened once at the start and then kept under
          watch, so the relationship is reviewed rather than assumed.
        </p>
      </div>
      <div class="rounded-[10px] bg-[#25282D] px-[20px] pb-[24px] pt-[20px] md:col-span-2">
        <h3 class="text-[19px] font-bold leading-[1.3] text-white">
          When an address turns into a case
        </h3>
        <p class="mt-[10px] text-[15px] leading-[1.7] text-white/70">
          Once something has gone wrong, screening becomes evidence work: the reports behind an
          address are what a documented answer to a regulator or a law-enforcement request is built
          from, and shared access lets more than one analyst work the same wallet.
        </p>
      </div>
    </div>
  </div>
</section>

<section class="bg-star-gray-600 pb-[60px] lg:pb-[80px]">
  <div class="mx-auto max-w-[1000px] px-[20px]">
    <h2 class="text-[24px] font-medium leading-[1.3] text-white lg:text-[28px] xl:text-[32px]">
      Screening is one part of a compliance stack
    </h2>
    <p class="mt-[20px] text-[16px] leading-[1.8] text-white/80">
      On its own, screening answers questions one address at a time. It becomes a control when it
      sits next to ongoing monitoring, alerts that reach the right people, roles for the team and a
      record that survives an audit. If that is the picture you are assembling, the
      <a routerLink="/aml-compliance-software" class="text-white underline underline-offset-4"
        >AML compliance software overview</a
      >
      describes how the whole platform fits together.
    </p>
  </div>
</section>

<section class="bg-star-gray-600 pb-[60px] lg:pb-[80px]">
  <div class="mx-auto max-w-[1000px] px-[20px]">
    <h2 class="text-[24px] font-medium leading-[1.3] text-white lg:text-[28px] xl:text-[32px]">
      Wallet screening: common questions
    </h2>
    <div class="mt-[28px] flex flex-col gap-[24px]">
      <div>
        <h3 class="text-[18px] font-bold leading-[1.4] text-white">
          What is crypto wallet screening?
        </h3>
        <p class="mt-[8px] text-[16px] leading-[1.8] text-white/70">
          It is the assessment of a crypto address against AML data: what its funds have been
          exposed to, and which flows it has taken part in. The output is a risk picture of the
          address that a compliance team uses to decide whether to accept, delay or refuse a
          transaction.
        </p>
      </div>
      <div>
        <h3 class="text-[18px] font-bold leading-[1.4] text-white">
          How is screening different from transaction monitoring?
        </h3>
        <p class="mt-[8px] text-[16px] leading-[1.8] text-white/70">
          Screening is something you do to an address at a chosen moment. Monitoring is what keeps
          happening to it afterwards — the wallet stays under watch and the platform raises an alert
          when its activity changes. Most teams need both, and in AMLOfficer a screened address can
          be moved onto automated monitoring directly.
        </p>
      </div>
      <div>
        <h3 class="text-[18px] font-bold leading-[1.4] text-white">
          Which data stands behind a screening result?
        </h3>
        <p class="mt-[8px] text-[16px] leading-[1.8] text-white/70">
          AMLOfficer aggregates four AML data providers, including BitOK, Global Ledger and Beosin.
          A single request goes out to all of them, so the result is not limited to one provider's
          view of the address.
        </p>
      </div>
      <div>
        <h3 class="text-[18px] font-bold leading-[1.4] text-white">
          Can we screen wallets from our own system?
        </h3>
        <p class="mt-[8px] text-[16px] leading-[1.8] text-white/70">
          Yes. Alongside the dashboard and Telegram, AMLOfficer offers an API: a lighter integration
          for working with reports from the dashboard, and a path for pulling AML data into your own
          systems in real time when screening has to happen inside your product.
        </p>
      </div>
      <div>
        <h3 class="text-[18px] font-bold leading-[1.4] text-white">
          Do we need an account to screen a wallet?
        </h3>
        <p class="mt-[8px] text-[16px] leading-[1.8] text-white/70">
          Yes — screening runs inside the AMLOfficer application, and a business account is what
          gives your team the dashboard, the Telegram notifications, role management and API access.
        </p>
      </div>
    </div>
  </div>
</section>

<section class="bg-star-gray-600 pb-[80px] lg:pb-[110px]">
  <div
    class="mx-auto flex max-w-[1000px] flex-col items-start gap-[20px] rounded-[10px] border border-solid border-white/20 px-[20px] py-[32px] lg:flex-row lg:items-center lg:justify-between lg:px-[40px]"
  >
    <div>
      <h2 class="text-[22px] font-medium leading-[1.3] text-white lg:text-[26px]">
        Start screening your first wallet
      </h2>
      <p class="mt-[10px] max-w-[520px] text-[15px] leading-[1.7] text-white/70">
        Open a business account, add an address, and invite the colleagues who will work on it with
        you.
      </p>
    </div>
    <a
      [href]="createAccountHref()"
      target="_blank"
      class="btn btn-white btn-with-arrow min-h-[52px] w-full max-w-[280px] rounded-[8px] font-harmony font-semibold leading-[18px]"
    >
      <span class="text-[16px] tracking-[0.3px]">Open Business Account</span>
      <mat-icon
        svgIcon="second-arrow-right"
        class="ml-[13px] mr-[5px] max-h-[14px] max-w-[16px] text-black"
      ></mat-icon>
    </a>
  </div>
</section>

<app-related-pages [pages]="relatedPages"></app-related-pages>
